/**
 * Resignation of Director (M32106) mapper — AOD(changeOfficerMapper)와 동일한
 * snake_case / boolean / signers 배열 규칙으로 Word [[변수]] 에 맞춤.
 *
 * prepare-documents 는 formData 키 = docx [[placeholder]] exact match (대소문자 구분).
 * DATA_JSON 에는 이 formData 가 그대로 저장된다.
 */

export type ResignGoverningBasis = 'constitution' | 'articles' | 'third_schedule';
export type ResignDirectorMode = 'sole' | 'multiple';

export interface ResignDirectorRow {
  id: number;
  fullName: string;
  icPassportNo: string;
  residentialAddress: string;
  dateOfCessation: string;
  reason: string;
  idKind: 'malaysian' | 'foreigner';
}

export interface ResignMapperInput {
  values: Record<string, string>;
  rows: number[];
  reasons?: Record<number | string, string>;
  nationality?: Record<number | string, string>;
  directorMode?: ResignDirectorMode;
  basis?: ResignGoverningBasis;
  signers?: any[];
  /** 특정 director 의 Section 58C 서명자 (있으면 board signers 대신 사용) */
  signersForDoc?: any[];
  /** per-director 문서일 때 대상 director id */
  focusRowId?: number | null;
}

function trim(v: any): string {
  return String(v ?? '').trim();
}

/** UI 라벨 → 정규화 키 (공백/하이픈/대소문자 무시) */
function normalizeReasonKey(reason: string): string {
  return trim(reason).toLowerCase().replace(/[\s\-]+/g, '_');
}

type ReasonId =
  | 'resignation'
  | 'non_appointment'
  | 'removal'
  | 'disqualification'
  | 'retirement'
  | 'deceased';

function resolveReasonId(reason: string): ReasonId | '' {
  const norm = normalizeReasonKey(reason);
  if (norm === 'resignation') return 'resignation';
  if (norm === 'non_appointment' || norm === 'nonappointment') return 'non_appointment';
  if (norm === 'removal') return 'removal';
  if (norm === 'disqualification') return 'disqualification';
  if (norm === 'retirement') return 'retirement';
  if (norm === 'deceased' || norm === 'death') return 'deceased';
  return '';
}

/**
 * Reasons for cessation Word 바인딩.
 *  - reason_* / resignation 등 → ☒·☐ (표에서 [[reason_resignation]] 직접 치환)
 *  - is_reason_* / is_* → boolean ([[if is_reason_resignation]]…[[endif]])
 */
function reasonFlags(reason: string): Record<string, any> {
  const selected = resolveReasonId(reason);
  const defs: Array<{ id: ReasonId; boolKeys: string[]; glyphKeys: string[] }> = [
    {
      id: 'resignation',
      boolKeys: ['is_resignation', 'is_reason_resignation', 'reasonResignation'],
      glyphKeys: ['reason_resignation', 'resignation', 'cb_resignation', 'resignation_check', 'reason_resignation_box'],
    },
    {
      id: 'non_appointment',
      boolKeys: ['is_non_appointment', 'is_reason_non_appointment', 'reasonNonAppointment'],
      glyphKeys: ['reason_non_appointment', 'non_appointment', 'nonappointment', 'cb_non_appointment', 'non_appointment_check', 'reason_non_appointment_box'],
    },
    {
      id: 'removal',
      boolKeys: ['is_removal', 'is_reason_removal', 'reasonRemoval'],
      glyphKeys: ['reason_removal', 'removal', 'cb_removal', 'removal_check', 'reason_removal_box'],
    },
    {
      id: 'disqualification',
      boolKeys: ['is_disqualification', 'is_reason_disqualification', 'reasonDisqualification'],
      glyphKeys: ['reason_disqualification', 'disqualification', 'cb_disqualification', 'disqualification_check', 'reason_disqualification_box'],
    },
    {
      id: 'retirement',
      boolKeys: ['is_retirement', 'is_reason_retirement', 'reasonRetirement'],
      glyphKeys: ['reason_retirement', 'retirement', 'cb_retirement', 'retirement_check', 'reason_retirement_box'],
    },
    {
      id: 'deceased',
      boolKeys: ['is_deceased', 'is_death', 'is_reason_deceased', 'reasonDeceased'],
      glyphKeys: ['reason_deceased', 'deceased', 'death', 'cb_deceased', 'deceased_check', 'reason_deceased_box'],
    },
  ];

  const out: Record<string, any> = {};
  for (const d of defs) {
    const on = selected === d.id;
    for (const k of d.boolKeys) out[k] = on;
    for (const k of d.glyphKeys) out[k] = on ? '☒' : '☐';
  }
  return out;
}

function buildDirectorFlat(prefix: string, row: ResignDirectorRow): Record<string, any> {
  const isMalaysian = row.idKind === 'malaysian';
  return {
    [`${prefix}_full_name`]: row.fullName,
    [`${prefix}_name`]: row.fullName,
    [`${prefix}_ic_passport_no`]: row.icPassportNo,
    [`${prefix}_nric_or_passport_number`]: row.icPassportNo,
    [`${prefix}_nric_or_passport`]: row.icPassportNo,
    [`${prefix}_residential_address`]: row.residentialAddress,
    [`${prefix}_address`]: row.residentialAddress,
    [`${prefix}_date_of_cessation`]: row.dateOfCessation,
    [`${prefix}_cessation_date`]: row.dateOfCessation,
    [`${prefix}_reason`]: row.reason,
    [`${prefix}_is_malaysian`]: isMalaysian,
    [`${prefix}_is_foreigner`]: !isMalaysian,
  };
}

function collectRows(input: ResignMapperInput): ResignDirectorRow[] {
  const ids = Array.isArray(input.rows) && input.rows.length ? input.rows : [1];
  return ids.map((id) => ({
    id,
    fullName: trim(input.values[`dir${id}_full_name`]),
    icPassportNo: trim(input.values[`dir${id}_ic_passport_no`]),
    residentialAddress: trim(input.values[`dir${id}_residential_address`]),
    dateOfCessation: trim(input.values[`dir${id}_date_of_cessation`]),
    reason:
      trim(
        input.reasons?.[id]
          || input.reasons?.[String(id)]
          || input.values[`dir${id}_reason`]
          || input.values[`dir${id}_reason_for_cessation`]
          || '',
      ) || 'Resignation',
    idKind: (input.nationality?.[id] || input.nationality?.[String(id)] || 'malaysian') as 'malaysian' | 'foreigner',
  }));
}

function extractOldRegistrationNo(raw: string): string {
  const m = trim(raw).match(/\(([^)]+)\)\s*$/);
  return m ? trim(m[1]) : '';
}

/**
 * 공통 + DCR / Letter / 58C 공통으로 쓸 formData.
 * focusRowId 가 있으면 그 director 를 dir_* / resign_director_* 단일 placeholder 로 올린다.
 */
export function buildResignFormData(input: ResignMapperInput): Record<string, any> {
  const v = input.values || {};
  const companyName = trim(v.company_name);
  const companyRegistrationNoRaw = trim(v.company_registration_no);
  // 문서에는 "신 (구)" 표기일 수 있음 → 순수 등록번호 / 구번호 분리
  const companyRegNoPure =
    companyRegistrationNoRaw.replace(/\s*\(.*\)\s*$/, '').trim() || companyRegistrationNoRaw;
  const oldRegistrationNo = extractOldRegistrationNo(companyRegistrationNoRaw);
  const companyAddress = trim(v.company_address);
  const constitutionClauseNumber = trim(v.constitution_clause_number);
  const resolutionDate = trim(v.resolution_date);
  const letterDate = trim(v.letter_date) || resolutionDate;

  const govType: ResignGoverningBasis = input.basis || 'constitution';
  const directorMode: ResignDirectorMode = input.directorMode || 'sole';
  const isSoleCompany = directorMode === 'sole';

  const allRows = collectRows(input);
  const focusId = input.focusRowId != null ? Number(input.focusRowId) : null;
  const focusRow =
    (focusId != null ? allRows.find((r) => r.id === focusId) : null) || allRows[0] || {
      id: 1,
      fullName: '',
      icPassportNo: '',
      residentialAddress: '',
      dateOfCessation: '',
      reason: 'Resignation',
      idKind: 'malaysian' as const,
    };

  const resolutionBasisClause =
    govType === 'third_schedule'
      ? 'the Third Schedule of the Companies Act 2016'
      : govType === 'articles'
        ? constitutionClauseNumber
          ? `Article ${constitutionClauseNumber} of the Articles of Association`
          : 'the Articles of Association'
        : constitutionClauseNumber
          ? `Clause ${constitutionClauseNumber} of the Constitution`
          : 'the Constitution';

  // DCR 반복부 — 템플릿 [[if resigning_directors]] … [[/if resigning_directors]]
  //   내부: [[director_full_name]], [[director_nric_or_passport_number]],
  //         [[if director_is_malaysian]] / [[if director_is_foreigner]]
  const resigningLoop = allRows.map((row) => ({
    ...buildDirectorFlat('director', row),
    ...buildDirectorFlat('dir', row),
    ...buildDirectorFlat('resign_director', row),
    ...buildDirectorFlat('resigning_director', row),
    director_full_name: row.fullName,
    director_name: row.fullName,
    director_nric_or_passport_number: row.icPassportNo,
    director_is_malaysian: row.idKind === 'malaysian',
    director_is_foreigner: row.idKind !== 'malaysian',
    full_name: row.fullName,
    name: row.fullName,
    ic_passport_no: row.icPassportNo,
    nric_or_passport: row.icPassportNo,
    residential_address: row.residentialAddress,
    date_of_cessation: row.dateOfCessation,
    cessation_date: row.dateOfCessation,
    reason: row.reason,
    reason_for_cessation: row.reason,
    is_malaysian: row.idKind === 'malaysian',
    is_foreigner: row.idKind !== 'malaysian',
    ...reasonFlags(row.reason),
  }));

  const multipleResigning = allRows.length > 1;
  const singleResigning = !multipleResigning;

  // 1) UI 원본 키(dir1_* 등)를 먼저 넣고
  // 2) 아래 정규화 키가 덮어쓰게 함 — company_registration_no "신 (구)" 가 순수값을 덮지 않도록.
  const rawFilled = Object.fromEntries(
    Object.entries(v).filter(([k, val]) => k && val != null && String(val).trim() !== ''),
  );

  const out: Record<string, any> = {
    ...rawFilled,

    // ── 회사 ──
    company_name: companyName,
    company_registration_no: companyRegNoPure,
    registration_number: companyRegNoPure,
    old_registration_no: oldRegistrationNo || trim(v.old_registration_no) || '',
    company_address: companyAddress,
    registered_address: companyAddress,
    company_registered_address: companyAddress,

    // ── 정관 / 결의 근거 ──
    constitution_clause_number: constitutionClauseNumber,
    constitution_clause_no: constitutionClauseNumber,
    resolution_basis_clause: resolutionBasisClause,
    governing_document_clause: resolutionBasisClause,

    constitution: govType === 'constitution',
    articles: govType === 'articles',
    third_schedule: govType === 'third_schedule',
    resolution_basis_constitution: govType === 'constitution',
    resolution_basis_articles: govType === 'articles',
    resolution_basis_third_schedule: govType === 'third_schedule',

    // 회사 director 수 (결의문 I/We) — UI directorMode
    is_sole_director_company: isSoleCompany,
    is_multiple_director_company: !isSoleCompany,
    single_director_company: isSoleCompany,
    multiple_directors_company: !isSoleCompany,
    single_director: isSoleCompany,
    multiple_directors: !isSoleCompany,
    // camelCase 폴백 (구 mapper)
    soleDirector: isSoleCompany,
    multipleDirectors: !isSoleCompany,
    basisConstitution: govType === 'constitution',
    basisArticles: govType === 'articles',
    basisThirdSchedule: govType === 'third_schedule',
    directorMode,
    basis: govType,

    // 사임 director 단/복수 — 템플릿 정식 키 (제목 RESIGNATION OF DIRECTOR/S, is/are)
    //   [[if resign_single_director]]Director[[endif]]
    //   [[if resign_multiple_directors]]Directors[[endif]]
    resign_single_director: singleResigning,
    resign_multiple_directors: multipleResigning,
    // 별칭
    resigning_single: singleResigning,
    resigning_multiple: multipleResigning,
    resigning_single_director: singleResigning,
    resigning_multiple_directors: multipleResigning,

    // ── 날짜 ──
    resolution_date: resolutionDate,
    letter_date: letterDate,
    resignation_date: focusRow.dateOfCessation || letterDate,
    date_of_declaration: resolutionDate,
    date_of_cessation: focusRow.dateOfCessation,
    cessation_date: focusRow.dateOfCessation,

    // ── 반복부 (DCR) — 템플릿 [[if resigning_directors]] ──
    resigning_directors: resigningLoop,
    resign_directors: resigningLoop,

    // ── 단일 director placeholder (Letter / 58C / DCR 1명) ──
    ...buildDirectorFlat('director', focusRow),
    ...buildDirectorFlat('dir', focusRow),
    ...buildDirectorFlat('resign_director', focusRow),
    ...buildDirectorFlat('resigning_director', focusRow),
    director_full_name: focusRow.fullName,
    director_nric_or_passport_number: focusRow.icPassportNo,
    director_is_malaysian: focusRow.idKind === 'malaysian',
    director_is_foreigner: focusRow.idKind !== 'malaysian',
    resign_director_name: focusRow.fullName,
    resign_director_address: focusRow.residentialAddress,
    director_name: focusRow.fullName,
    officer_name: focusRow.fullName,
    reason: focusRow.reason,
    reason_for_cessation: focusRow.reason,
    reasons_for_cessation: focusRow.reason,
    cessation_reason: focusRow.reason,
    ...reasonFlags(focusRow.reason),
    is_malaysian: focusRow.idKind === 'malaysian',
    is_foreigner: focusRow.idKind !== 'malaysian',

    // UPPER_CASE alias
    COMPANY_NAME: companyName,
    COMPANY_REGISTRATION_NO: companyRegNoPure,
    COMPANY_ADDRESS: companyAddress,
    RESOLUTION_DATE: resolutionDate,
    LETTER_DATE: letterDate,
    RESIGN_DIRECTOR_NAME: focusRow.fullName,
    DATE_OF_CESSATION: focusRow.dateOfCessation,

    _signerLayout: 'box',
  };

  // signers — 배열로 (AOD와 동일). JSON 문자열이면 템플릿/백엔드가 깨질 수 있음.
  const signersSrc = Array.isArray(input.signersForDoc) && input.signersForDoc.length
    ? input.signersForDoc
    : Array.isArray(input.signers)
      ? input.signers
      : [];
  if (signersSrc.length > 0) {
    out.signers = signersSrc.map((s: any) => ({ ...s, _signerLayout: 'box' }));
  }

  return out;
}

/** @deprecated — 패널이 ResignMapperInput 형태로 호출하도록 교체됨. 호환용 래퍼. */
export function buildResignFormDataLegacy(
  values: Record<string, string>,
  local: {
    directorMode?: string;
    basis?: string;
    reasons?: Record<number | string, string>;
    nationality?: Record<number | string, string>;
    signers?: any[];
    signers58?: Record<string, any[]>;
    rows?: number[];
  },
): Record<string, any> {
  return buildResignFormData({
    values,
    rows: local.rows || [1],
    reasons: local.reasons,
    nationality: local.nationality,
    directorMode: (local.directorMode as ResignDirectorMode) || 'sole',
    basis: (local.basis as ResignGoverningBasis) || 'constitution',
    signers: local.signers,
  });
}
